import { categories, hotTopics, ranking } from '@/mocks/heiliao';

interface RankingSectionProps {
  activeCategory: string;
  onChangeCategory: (name: string) => void;
}

const trendIcon: Record<string, { icon: string; cls: string }> = {
  up: { icon: 'ri-arrow-up-line', cls: 'text-primary-600' },
  down: { icon: 'ri-arrow-down-line', cls: 'text-secondary-500' },
  flat: { icon: 'ri-subtract-line', cls: 'text-foreground-400' },
};

const tabList = ['全部', ...categories.map((c) => c.name)];

export default function RankingSection({ activeCategory, onChangeCategory }: RankingSectionProps) {
  const filteredRanking =
    activeCategory === '全部' ? ranking : ranking.filter((item) => item.category === activeCategory);
  const filteredTopics =
    activeCategory === '全部' ? hotTopics : hotTopics.filter((item) => item.category === activeCategory);

  return (
    <section id="ranking" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        <div className="reveal flex flex-col gap-5 border-b-2 border-foreground-950 pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="w-full">
            <span className="text-xs font-semibold tracking-[0.3em] text-primary-600">TRENDING</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              热门内容热度排行
            </h2>
          </div>
          <p className="max-w-lg text-sm leading-relaxed text-foreground-600">
            热度值综合公开讨论量、更新频率与互动规模排序，仅供关注度参考，不代表事件性质或信息真实性。
          </p>
        </div>

        <div className="reveal mt-6 flex flex-wrap gap-2 rounded-full border border-background-300 bg-background-50 px-1 py-1">
          {tabList.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => onChangeCategory(name)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors md:text-sm ${
                activeCategory === name
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.15fr]">
          <div className="reveal rounded-lg border border-background-300 bg-background-50 p-5 md:p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-bar-chart-2-line text-primary-600"></i>
              实时榜单
              <span className="ml-1 rounded-sm bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800">
                {activeCategory}
              </span>
            </h3>

            {filteredRanking.length === 0 ? (
              <p className="mt-6 rounded-md bg-background-100 px-4 py-8 text-center text-sm text-foreground-500">
                该分区暂无上榜内容，试试切换到其他分区。
              </p>
            ) : (
              <ol className="mt-4 divide-y divide-background-200">
                {filteredRanking.map((item, index) => {
                  const trend = trendIcon[item.trend];
                  return (
                    <li key={item.title} className="flex items-start gap-3.5 py-3.5">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm font-heading text-xs font-bold ${
                          index < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-snug text-foreground-900">{item.title}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                          <span className="rounded-sm bg-background-200 px-1.5 py-0.5">{item.category}</span>
                          <span className={`flex items-center ${trend.cls}`}>
                            <i className={trend.icon}></i>
                            {item.trend === 'up' ? '上升' : item.trend === 'down' ? '回落' : '持平'}
                          </span>
                          <span className="text-foreground-400">热度 {item.heat}</span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          <div>
            <h3 className="reveal mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-article-line text-primary-600"></i>
              精选话题
            </h3>
            {filteredTopics.length === 0 ? (
              <p className="rounded-md border border-background-300 bg-background-50 px-4 py-8 text-center text-sm text-foreground-500">
                该分区暂无精选话题。
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {filteredTopics.map((topic) => (
                  <article
                    key={topic.id}
                    className="flex flex-col rounded-lg border border-background-300 bg-background-50 p-5 transition-colors hover:border-primary-300"
                  >
                    <div className="flex items-center justify-between">
                      <span className="rounded-sm bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">
                        {topic.tag}
                      </span>
                      <span className="text-[11px] text-foreground-400">{topic.time}</span>
                    </div>
                    <h4 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950">
                      {topic.title}
                    </h4>
                    <p className="mt-2 flex-1 text-xs leading-relaxed text-foreground-600">{topic.summary}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                      <span className="text-[11px] text-foreground-500">{topic.category}</span>
                      <span className="flex items-center gap-1 text-[11px] text-primary-600">
                        <i className="ri-fire-line"></i>
                        热度 {topic.heat}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}